/* eslint-disable react/display-name */
import { ResultFormat } from "components/Button/ResultFormatButton/ResultFormatButton";
import { getMaxCitations, Paper, PaperDetails, SearchMode } from "helpers/api";
import { SearchResultTextType } from "helpers/search";
import {
  MixpanelActionSource,
  MixpanelCiteType,
  MixpanelFullTextLinkType,
} from "helpers/services/mixpanel/events";
import { debounce } from "lodash";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { PaperQuote } from "store/slices/citedPapers";
import { YesNoType } from "types/YesNoType";
import SearchResultsListLoading from "./components/SearchResultsListLoading";

import {
  FREE_USER_SNAPSHOT_AUTO_FETCH_LIMIT,
  PRO_USER_SNAPSHOT_AUTO_FETCH_LIMIT,
  DEEP_USER_SNAPSHOT_AUTO_FETCH_LIMIT,
  SEARCH_RESULT_TABLE_COLUMNS,
  SearchResultTableColumn,
} from "./components/SearchResult/constants";
import SearchResult from "./components/SearchResult";
import { Text } from "design_system";
import { SubscriptionPlanIcon } from "features/Pricing";
import { CoButton, ButtonVariant } from "components/CoButton";
import { CreditedFeature } from "store/slices/subscription";
import { usePremiumFeatureModal } from "features/Subscription/components";
import useLabels from "hooks/useLabels";
import TableResultsSection from "./components/TableResultsSection";
import DefaultResultsSection from "./components/DefaultResultsSection";
import useSubscription from "features/User/hooks/useSubscription";

type SearchResultsListProps = {
  children?: ReactNode;
  childrenIndex?: number; // Interleave other components, such as related searches, within the results at a specific index using child components.
  includeCitations?: boolean;
  isLoading: boolean;
  isLoadingExtractedAnswers?: boolean;
  isLoadingTakeawayTranslations?: boolean;
  shouldFetchTakeawayTranslations?: boolean;
  shouldBlockEnglishTakeaway?: boolean;
  items: Paper[];
  onClickAskPaper?: (
    item: Paper,
    index: number,
    resultType?: SearchResultTextType
  ) => void;
  onClickItem?: (
    item: Paper,
    index: number,
    resultType?: SearchResultTextType
  ) => void;
  onCitePaper?: (
    item: Paper,
    citeType: MixpanelCiteType,
    index: number,
    resultType?: SearchResultTextType
  ) => void;
  onFullTextClick?: (
    paper: Paper | PaperDetails,
    linkType: MixpanelFullTextLinkType,
    actionSource: MixpanelActionSource,
    index?: number,
    libraryId?: string
  ) => void;
  onOpenStudySnapshot?: (
    item: Paper,
    index: number,
    resultType?: SearchResultTextType
  ) => void;
  onSharePaper?: (
    item: Paper,
    index: number,
    shareType: string,
    resultType?: SearchResultTextType
  ) => void;
  resultIdToExtractedAnswer?: { [resultId: string]: string };
  resultIdToTakeawayTranslation?: { [resultId: string]: string };
  resultIdToYesNoAnswer?: { [resultId: string]: YesNoType };
  searchQuery?: { [key: string]: string | string[] | undefined };
  includeDoi?: boolean;
  showSaveBtn?: boolean;
  showCiteBtn?: boolean;
  showShareBtn?: boolean;
  showRecentlyUpdated?: boolean;
  mixpanelActionSource?: MixpanelActionSource;
  onClickSave?: (item: Paper) => void;
  interactionId?: string;
  resultFormat?: ResultFormat;
  isProResearch?: boolean;
  externalLinks?: boolean;
  showCitationDashes?: boolean;
  openDetailsInNewTab?: boolean;
  searchMode?: SearchMode;
  isComplete?: boolean;
  isDeepSearchExecuting?: boolean;
  paperQuotes?: Record<string, PaperQuote[]>; // paperId -> quotes
  usePaperSidebar?: boolean;
};

/**
 * @component SearchResultsList
 * @description Container component for result items
 * @example
 * return (
 *   <SearchResultsList items={[...list]} />
 * )
 */
const SearchResultsList = React.memo(
  ({
    children,
    childrenIndex,
    includeCitations = false,
    isLoading,
    isLoadingExtractedAnswers,
    isLoadingTakeawayTranslations,
    shouldFetchTakeawayTranslations,
    shouldBlockEnglishTakeaway,
    items,
    onClickAskPaper,
    onClickItem,
    onCitePaper,
    onFullTextClick,
    onOpenStudySnapshot,
    onSharePaper,
    resultIdToExtractedAnswer,
    resultIdToTakeawayTranslation,
    resultIdToYesNoAnswer,
    searchQuery,
    includeDoi = false,
    showSaveBtn = false,
    showCiteBtn = false,
    showShareBtn = false,
    showRecentlyUpdated = false,
    mixpanelActionSource,
    onClickSave,
    interactionId,
    resultFormat,
    isProResearch = false,
    externalLinks = false,
    showCitationDashes = false,
    openDetailsInNewTab = false,
    searchMode = SearchMode.PRO_ANALYSIS,
    isComplete = false,
    isDeepSearchExecuting = false,
    paperQuotes,
    usePaperSidebar = false,
  }: SearchResultsListProps) => {
    const maxCitationsAllowed = getMaxCitations(searchMode);
    const zoteroItemUpdated = debounce(() => {
      // Force Zotero to refresh metadata - When the display is loaded (component did mount), trigger a ZoteroItemUpdated event to instruct Zotero to re-detect metadata on the page.
      // https://www.zotero.org/support/dev/exposing_metadata#force_zotero_to_refresh_metadata
      document.dispatchEvent(
        new Event("ZoteroItemUpdated", {
          bubbles: true,
          cancelable: true,
        })
      );
    }, 1000);

    const [labels] = useLabels("features.thread-interaction-results");

    const { isFreePlan, subscriptionTier } = useSubscription();
    const isProPlan = subscriptionTier === "premium";
    const isDeepPlan = subscriptionTier === "deep";

    const { openPremiumFeatureModal } = usePremiumFeatureModal();

    const onClickUpgradeToPremium = () => {
      openPremiumFeatureModal({
        feature: CreditedFeature.TABLE_EXTRACTION,
        actionSource: MixpanelActionSource.ResultsTableView,
      });
    };

    const [prevItems, setPrevItems] = useState<Paper[]>([]);
    const [tableColumns, setTableColumns] = useState<SearchResultTableColumn[]>(
      SEARCH_RESULT_TABLE_COLUMNS
    );

    // --- Batch Animation State ---
    const [recentlyAddedIds, setRecentlyAddedIds] = useState<string[]>([]);
    const [lastBatchTime, setLastBatchTime] = useState<number>(0);
    const [visibleItems, setVisibleItems] = useState<Paper[]>(items);
    const [finalRankingFlashIds, setFinalRankingFlashIds] = useState<string[]>(
      []
    );

    // Update prevItems when items change
    useEffect(() => {
      setPrevItems([...items]);
    }, [items]);

    const updatedResults = useMemo(() => {
      if (!showRecentlyUpdated) return {};

      const updatedResults: { [id: string]: boolean } = {};

      // Identify new results by comparing with previous items
      items.forEach((item) => {
        const prevIndex = prevItems.findIndex(
          (prevItem) => prevItem.paper_id === item.paper_id
        );

        // Mark as updated if it's a new result
        if (prevIndex === -1) {
          updatedResults[item.paper_id] = true;
        }
      });

      return updatedResults;
      // only trigger when prevItems changes, not when items changes
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showRecentlyUpdated, prevItems]);

    useEffect(() => {
      if (items.length > 0) {
        const doiElement = document.getElementsByClassName("zotero-doi");
        if (doiElement.length > 0) {
          zoteroItemUpdated();
        }
      }
    }, [items, zoteroItemUpdated]);

    const getPaperIds = (papers: Paper[]) => papers.map((p) => p.paper_id);

    // Throttle batch additions to 2s apart
    useEffect(() => {
      // Only apply flashing animations for PRO_RESEARCH mode
      const shouldApplyFlashing = searchMode === SearchMode.PRO_RESEARCH;

      if (!isProResearch || isComplete) {
        setVisibleItems(items);
        setRecentlyAddedIds([]);
        if (isComplete && isProResearch && shouldApplyFlashing) {
          setFinalRankingFlashIds(getPaperIds(items));
          setTimeout(() => setFinalRankingFlashIds([]), 300);
        } else {
          setFinalRankingFlashIds([]);
        }
        return;
      }
      // Find new papers (not in visibleItems)
      const currentIds = new Set(getPaperIds(visibleItems));
      const newPapers = items.filter((p) => !currentIds.has(p.paper_id));
      if (newPapers.length === 0) return;
      const now = Date.now();
      if (now - lastBatchTime < 2000) {
        // Wait until 2s have passed since last batch
        const timeout = setTimeout(
          () => {
            // If Deep Search is currently executing, add new papers to the start of the list
            if (isDeepSearchExecuting) {
              setVisibleItems((prev) => [...newPapers, ...prev]);
            } else {
              setVisibleItems((prev) => [...prev, ...newPapers]);
            }
            if (shouldApplyFlashing) {
              setRecentlyAddedIds(getPaperIds(newPapers));
              setLastBatchTime(Date.now());
              setTimeout(() => setRecentlyAddedIds([]), 300);
            }
          },
          2000 - (now - lastBatchTime)
        );
        return () => clearTimeout(timeout);
      } else {
        // If Deep Search is currently executing, add new papers to the start of the list
        if (isDeepSearchExecuting) {
          setVisibleItems((prev) => [...newPapers, ...prev]);
        } else {
          setVisibleItems((prev) => [...prev, ...newPapers]);
        }

        if (shouldApplyFlashing) {
          setRecentlyAddedIds(getPaperIds(newPapers));
          setLastBatchTime(Date.now());
          setTimeout(() => setRecentlyAddedIds([]), 300);
        }
      }
      // eslint-disable-next-line
    }, [items, searchMode]);

    // Get a subset of the result list to display in sections with a specific format
    const getResults = (
      startIndex?: number,
      endIndex?: number,
      format?: ResultFormat
    ) => (
      <>
        {visibleItems
          .slice(startIndex || 0, endIndex || visibleItems.length)
          .map((item, index) => {
            const isFinalRanking = finalRankingFlashIds.includes(item.paper_id);
            const isRecentlyAdded =
              !isFinalRanking && recentlyAddedIds.includes(item.paper_id);
            const trueIndex = index + (startIndex || 0);

            return (
              <React.Fragment key={item.paper_id}>
                <SearchResult
                  onClickAskPaper={onClickAskPaper}
                  onClickItem={onClickItem}
                  onCitePaper={onCitePaper}
                  onFullTextClick={onFullTextClick}
                  onOpenStudySnapshot={onOpenStudySnapshot}
                  onSharePaper={onSharePaper}
                  showShareBtn={showShareBtn}
                  showCiteBtn={showCiteBtn}
                  showSaveBtn={showSaveBtn}
                  recentlyUpdated={updatedResults[item.paper_id]}
                  recentlyAdded={isRecentlyAdded}
                  finalRanking={isFinalRanking}
                  index={trueIndex}
                  extractedAnswer={resultIdToExtractedAnswer?.[item.paper_id]}
                  takeawayTranslation={
                    resultIdToTakeawayTranslation?.[item.paper_id]
                  }
                  isLoadingTakeawayTranslation={
                    isLoadingTakeawayTranslations &&
                    !resultIdToTakeawayTranslation?.[item.paper_id]
                  }
                  shouldFetchTakeawayTranslation={
                    shouldFetchTakeawayTranslations
                  }
                  shouldBlockEnglishTakeaway={shouldBlockEnglishTakeaway}
                  item={item}
                  citation={
                    includeCitations && trueIndex < maxCitationsAllowed
                      ? (item.originalIndex ?? trueIndex)
                      : undefined
                  }
                  meter={resultIdToYesNoAnswer?.[item.paper_id]}
                  isLoadingExtractedAnswer={isLoadingExtractedAnswers}
                  searchQuery={searchQuery}
                  showPaperDoi={includeDoi}
                  hashPaperRecordId={item.paper_id}
                  mixpanelActionSource={mixpanelActionSource}
                  showManageUploadedPaperBtn={false}
                  onClickSave={onClickSave}
                  interactionId={interactionId}
                  resultFormat={format}
                  externalLink={externalLinks}
                  showCitationDashes={showCitationDashes}
                  openDetailsInNewTab={openDetailsInNewTab}
                  directQuotes={paperQuotes?.[item.paper_id] ?? []}
                  usePaperSidebar={usePaperSidebar}
                />
                {childrenIndex === trueIndex ? children : null}
              </React.Fragment>
            );
          })}
        {(isLoading || visibleItems.length === 0) && (
          <SearchResultsListLoading resultFormat={format} />
        )}
      </>
    );

    // Get a results section for the given format
    const getResultsSection = (
      startIndex?: number,
      endIndex?: number,
      format?: ResultFormat
    ) => {
      const Wrapper =
        format === ResultFormat.TABLE
          ? TableResultsSection
          : DefaultResultsSection;
      return (
        <Wrapper tableColumns={tableColumns}>
          {getResults(startIndex, endIndex, format)}
        </Wrapper>
      );
    };

    // Get the final contructed list of results with the right sections
    const getFinalList = () => {
      const userAutoFetchLimit = isFreePlan
        ? FREE_USER_SNAPSHOT_AUTO_FETCH_LIMIT
        : isDeepPlan
          ? DEEP_USER_SNAPSHOT_AUTO_FETCH_LIMIT
          : PRO_USER_SNAPSHOT_AUTO_FETCH_LIMIT;

      // If the user's desired format is table, we need to handle free and premium cases
      if (resultFormat === ResultFormat.TABLE) {
        // Free users can see the first 3 papers in table view, and the rest in compact view.
        // Pro users can see the first 20 papers in table view, and the rest in compact view.

        // There is a banner between the two sections to upgrade to the next tier.
        // Excluding Team and Enterprise users until they can upgrade to Deep individually.
        if (isFreePlan || isProPlan) {
          return (
            <>
              {visibleItems.length > userAutoFetchLimit
                ? getResultsSection(0, userAutoFetchLimit, ResultFormat.TABLE)
                : getResultsSection(0, undefined, ResultFormat.TABLE)}
              <div className="flex flex-col w-full items-center px-4">
                <div className="flex w-full py-4 gap-6 items-center justify-between max-w-page border-b">
                  <Text size="sm" weight="medium" className="flex-1">
                    {isFreePlan
                      ? labels["extraction-paywall-free"]
                      : labels["extraction-paywall-pro"]}
                  </Text>
                  <CoButton
                    icon={
                      <SubscriptionPlanIcon
                        plan={isFreePlan ? "premium" : "deep"}
                      />
                    }
                    variant={ButtonVariant.Tertiary}
                    label={
                      isFreePlan
                        ? labels["upgrade-to-pro"]
                        : labels["upgrade-to-deep"]
                    }
                    onClick={onClickUpgradeToPremium}
                    size="sm"
                  />
                </div>
              </div>
              {visibleItems.length > userAutoFetchLimit &&
                getResultsSection(
                  userAutoFetchLimit,
                  undefined,
                  ResultFormat.COMPACT
                )}
            </>
          );
        } else {
          // Show the two sections without a banner between them.
          return (
            <>
              {visibleItems.length > userAutoFetchLimit
                ? getResultsSection(0, userAutoFetchLimit, ResultFormat.TABLE)
                : getResultsSection(0, undefined, ResultFormat.TABLE)}
              {visibleItems.length > userAutoFetchLimit &&
                getResultsSection(
                  userAutoFetchLimit,
                  undefined,
                  ResultFormat.COMPACT
                )}
            </>
          );
        }
      }

      // If the user's desired format is not table, we can just return the full list of
      // results in the desired format
      return getResultsSection(0, undefined, resultFormat);
    };

    return getFinalList();
  }
);

export default SearchResultsList;
